import React, { useState } from "react";
import { ForwardedRef } from "react";
import { convertDateTimeToString, convertDateToString, convertTimeToString } from "../../utility/ConvertUtily";
import { CheckBox } from "../checkBox/CheckBox";
import {
  RadioButton,
  RadioButtonItem,
} from "../radiobutton/RadioButton";

type props = {
  id: string;
  label?: string;
  type?: string;
  className?: string;
  inputClassName?: string;
  children?: React.ReactNode;
  value?: any;
  dot?: boolean;
  onChange?: React.ChangeEventHandler<HTMLInputElement> | any;
  color?: "blue" | "green" | "red" | "yellow" | "indigo";
  radioButtonItem?: RadioButtonItem[];
  [key: string]: any;
  error?: boolean;
  helperText?: string;
  required?: boolean;
  errMsgWhitespaceWrap?: string;
};

type lableProps = {
  id: string;
  label?: string;
  type?: string;
  className?: string;

  children?: React.ReactNode;
  color?: "blue" | "green" | "red" | "yellow" | "indigo";
  [key: string]: any;
};

const styles = {
  rounded: `rounded-full`,
  block: `flex justify-center w-full`,
  error: `border-red-300 ring-red-500 ring-1 focus:ring-red-500 focus:border-red-500`,
  errorMessage: `text-xs text-red-500 mt-2`,
  default: `text-white focus:outline-none shadow font-medium transition ease-in duration-200`,
  disabled: `opacity-60 cursor-not-allowed`,
  sizes: {
    sm: "px-6 py-1 text-sm",
    md: "px-6 py-2",
    lg: "px-6 py-3 text-lg",
  },
  colors: {
    blue: "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
    green: "border-gray-300 focus:border-green-500 focus:ring-green-500",
    red: "border-gray-300 focus:border-red-500 focus:ring-red-500",
    yellow: "border-gray-300 focus:border-yellow-500 focus:ring-yellow-500",
    indigo: "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500",
  },
  errMsgWhitespaceWrap: "whitespace-nowrap",
};

export const Label = ({
  id,
  children,
  className = "mb-1 block text-sm font-medium text-gray-700",
}: lableProps) => {
  return (
    <label htmlFor={id} className={className}>
      {children}
    </label>
  );
};

type FieldRef = ForwardedRef<never>;
export const TextField = React.forwardRef(
  (
    {
      id,
      label,
      type = "text",
      className = "h-20",
      inputClassName,
      value,
      onChange,
      color = "indigo",
      error,
      helperText,
      radioButtonItem,
      required,
      errMsgWhitespaceWrap,
      ...props
    }: props,
    ref: FieldRef
  ) => {
    let component;
    if (type === "textarea") {
      component = (
        <TextArea
          id={id}
          type={type}
          {...props}
          value={value}
          onChange={onChange}
          error={error}
        />
      );
    } else if (type === "checkbox") {
      component = (
        <CheckBox
          id={id}
          type={type}
          {...props}
          value={value}
          onChange={onChange}
          error={error}
        >
          {label}
        </CheckBox>
      );
    } else if (type === "radiobutton") {
      component = (
        <RadioButton
          name={id}
          radioButtonItem={radioButtonItem!}
          id={id}
          type={type}
          {...props}
          value={value}
          onChange={onChange}
          error={error}
          label={label}
        >
          {label}
        </RadioButton>
      );
    } else if(type === "password") {
      component = (
        <PasswordForm
          name={id}
          radioButtonItem={radioButtonItem!}
          id={id}
          type={type}
          {...props}
          value={value}
          onChange={onChange}
          error={error}
          label={label}
        >
          {label}
        </PasswordForm>
      );
    } else {
      const toString = Object.prototype.toString
      if(type === "datetime-local" && toString.call(value) === '[object Date]'){
        value = convertDateTimeToString(value);
      }
      if(type === "date" && toString.call(value) === '[object Date]'){
        value = convertDateToString(value);
      }
      if(type === "time" && toString.call(value) === '[object Date]'){
        value = convertTimeToString(value);
      }
      component = (
        <Input
          className={inputClassName}
          id={id}
          type={type}
          {...props}
          value={value}
          onChange={onChange}
          error={error}
          errMsgWhitespaceWrap={errMsgWhitespaceWrap}
        />
      );
    }

    return (
      <div ref={ref} className={className}>
        {type !== "checkbox" && type !== "radiobutton" && (
          <div className="flex"><Label id={id}>{label}</Label>{required && <div className="text-red-500 mx-1">*</div>}</div>
        )}
        {component}
        {error && (
          <span role="alert" className={`${styles.errorMessage} ${errMsgWhitespaceWrap === undefined ? "whitespace-nowrap" : errMsgWhitespaceWrap}`}>
            {helperText}
          </span>
        )}
      </div>
    );
  }
);

export const Input = ({
  id,
  label,
  type = "text",
  className = "",
  value,
  onChange,
  color = "indigo",
  error,
  ...props
}: props) => {
  return (
    <input
      id={id}
      type={type}
      {...props}
      value={value}
      onChange={onChange}
      className={`p-1 border px-3 py-2 block w-full rounded-md shadow-sm sm:text-sm  ${
        error ? styles.error : styles.colors[color]
      }`}
    />
  );
};


export const PasswordForm = ({
  id,
  label,
  className = "",
  value,
  onChange,
  color = "indigo",
  error,
  required
}: props) => {
  const [isShow, setIsShow] = useState(false);
  // 表示
  const showPass = () => {
    setIsShow(true);
  };
  // 非表示
  const hidePass = () => {
    setIsShow(false);
  };

  return (
    <div className="flex border rounded-md shadow-sm mt-1">
      <input
        id={id}
        type={isShow ? "text" : "password"}
        className={`p-1 border px-3 py-2 block w-full rounded-md shadow-sm sm:text-sm  ${
          error ? styles.error : styles.colors[color]
        }`}
        placeholder="入力してください"
        value={value} onChange={onChange} required={required} />
      {!isShow &&
        <div className="flex items-center px-1 mx-3" onClick={showPass}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
            <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
          </svg>
        </div>
      }
      {isShow &&
        <div className="flex items-center px-1 mx-3" onClick={hidePass}>
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3.707 2.293a1 1 0 00-1.414 1.414l14 14a1 1 0 001.414-1.414l-1.473-1.473A10.014 10.014 0 0019.542 10C18.268 5.943 14.478 3 10 3a9.958 9.958 0 00-4.512 1.074l-1.78-1.781zm4.261 4.26l1.514 1.515a2.003 2.003 0 012.45 2.45l1.514 1.514a4 4 0 00-5.478-5.478z" clipRule="evenodd" />
            <path d="M12.454 16.697L9.75 13.992a4 4 0 01-3.742-3.741L2.335 6.578A9.98 9.98 0 00.458 10c1.274 4.057 5.065 7 9.542 7 .847 0 1.669-.105 2.454-.303z" />
          </svg>
        </div>
      }
    </div>
  );
};

//これは現状未使用
//改良しないと使えないかも
export const SelectField = ({
  id,
  label,
  className = "",
  color = "blue",
  value,
  onChange,
  ...props
}: props) => {
  return (
    <div className={className}>
      {label && <Label id={id}>{label}</Label>}
      <select
        id={id}
        {...props}
        className={`p-1 border h-8 block w-full rounded-md border-gray-300 shadow-sm text-sm ${styles.colors[color]}`}
      />
    </div>
  );
};

export const TextArea = ({
  dot,
  error,
  disabled,
  isResize,
  id,
  color = "indigo",
  value,
  onChange,
  ...props
}: props) => {
  return (
    <textarea
      aria-required={dot}
      aria-invalid={!!error}
      className={`h-full block w-full rounded-md  sm:text-sm  ${
        disabled ? styles.disabled : ""
      }
      ${
        error
          ? styles.error
          : "border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
      }
      ${
        isResize ? 'resize-auto' : 'resize-none'
      }`}
      disabled={disabled}
      id={id}
      name={id}
      value={value}
      onChange={onChange}
      {...props}
    />
  );
};
